import { autoAssignStatuses } from '~/app/pages/SurgeonScheduler/TablesLayout/shared'
import { day } from '~/astra'
import { Schemas } from '~/clients/api-client'
import {
    partialUpdatePractitionerScheduleLocation,
    partialUpdatePractitionerScheduleStatus,
    updateBatchPractitionerScheduleLocation,
} from '~/store/di-entity.api.generated'
import { useStore } from '~/store/store'
import { Practitioner, selectPractitionerScheduleLocations, usePractitionerScheduleLocations, usePractitionerScheduleStatuses } from '~/storm'

import { dndItemTypes, PractitionerActivityDragItem, ScheduleStatusDragItem, useTablesLayoutDrop } from '../../../dndData'

type Props = {
    practitionerId: Practitioner['id']
    date: string
}

export const useActivityDrop = ({ practitionerId, date }: Props) => {
    const statuses = usePractitionerScheduleStatuses({
        where: { practitioner_id: practitionerId, scheduled_date: date },
        include: { definition: true },
    })
    const scheduleLocationsByLocationId = usePractitionerScheduleLocations({
        where: { practitioner_id: practitionerId, scheduled_date: date },
        include: { location: true },
        keyBy: 'location_id',
    })

    const canDrop = (item: PractitionerActivityDragItem) => {
        if (item.data.scheduled_date === date && item.data.practitioner_id === practitionerId) return false

        if (item.type === dndItemTypes.practitionerScheduleLocation) {
            return !scheduleLocationsByLocationId.get(item.data.location_id)
        }
        return !statuses.some(s => s.definition?.code === item.data.status_code)
    }

    const handleDropScheduleStatus = async (item: ScheduleStatusDragItem) => {
        const state = useStore.getState()
        const promises: Promise<unknown>[] = [
            partialUpdatePractitionerScheduleStatus(item.data.id, {
                practitioner_id: practitionerId,
                scheduled_date: date,
                status_code: item.data.status_code,
                practitioner_status_definition_uuid: item.data.practitioner_status_definition_uuid,
            }),
        ]
        if (item.data.locations.length > 0) {
            const scheduleLocationsToMove = selectPractitionerScheduleLocations(state, {
                where: {
                    practitioner_uuid: item.data.practitioner_uuid,
                    scheduled_date: item.data.scheduled_date,
                    location_id: {
                        in: item.data.locations.map(location => location.id).filter(id => !scheduleLocationsByLocationId.has(id)),
                    },
                },
            })
            const batchData: Schemas['BaseEntitySchemaUpdate_PractitionerScheduleLocationCreate_'][] = scheduleLocationsToMove.map(sl => ({
                id: sl.id,
                uuid: sl.uuid,
                body: {
                    location_id: sl.location_id,
                    practitioner_id: practitionerId,
                    scheduled_date: date,
                },
            }))
            promises.push(updateBatchPractitionerScheduleLocation(batchData))
        }

        return Promise.all(promises)
    }

    const { drop, isActive, item } = useTablesLayoutDrop({
        id: `practitioner-activity:${practitionerId}:${date}`,
        type: [dndItemTypes.practitionerScheduleLocation, dndItemTypes.practitionerScheduleStatus],
        canDrop,
        onDrop: async item => {
            if (item.type === dndItemTypes.practitionerScheduleLocation) {
                await Promise.all([
                    partialUpdatePractitionerScheduleLocation(item.data.id, {
                        practitioner_id: practitionerId,
                        scheduled_date: date,
                        location_id: item.data.location_id,
                    }),
                    autoAssignStatuses([{ date: day(date), locationId: item.data.location_id, practitionerId }]),
                ])
                return
            }

            await handleDropScheduleStatus(item)
        },
    })

    return {
        isActive,
        toBeDroppedLocation: item?.type === dndItemTypes.practitionerScheduleLocation ? item.data : null,
        toBeDroppedStatus: item?.type === dndItemTypes.practitionerScheduleStatus ? item.data : null,
        drop,
    } as const
}
